import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import clsx from 'clsx';
import { useState } from 'react';

import { ATTRIBUTE_INDICATORS } from '#components/signal-chip/utils.js';
import { useParsedDirectiveRows } from './use-parsed-directive-rows.js';

import { DirectiveApplyBar } from './directive-apply-bar.js';

export interface SignalsTableMarkdownItemProps {
	/** Raw JSON string containing the signals array. */
	jsonData?: string;
}

interface SignalRow {
	signalName: string;
	direction?: string | null;
	power?: boolean;
	ground?: boolean;
	digital?: boolean;
	analog?: boolean;
	activeLow?: boolean;
}

type BooleanSignalKey = 'power' | 'ground' | 'digital' | 'analog' | 'activeLow';

interface BadgeConfig {
	key: BooleanSignalKey;
	label: string;
}

const BADGES: BadgeConfig[] = [
	{ key: 'power', label: 'PWR' },
	{ key: 'ground', label: 'GND' },
	{ key: 'digital', label: 'DIG' },
	{ key: 'analog', label: 'ANA' },
	{ key: 'activeLow', label: 'A‑L' },
];

const editInputClass =
	'w-full bg-transparent border-b border-gray-a6 focus:outline-none focus:border-blue-9 px-1 py-0.5';

function parseSignals(jsonData: string): SignalRow[] | null {
	try {
		const parsed: unknown = JSON.parse(jsonData);
		if (!Array.isArray(parsed)) {
			return null;
		}
		return parsed as SignalRow[];
	} catch {
		return null;
	}
}

/**
 * Markdown directive component that renders a compact signals table in a workbench message.
 *
 * Each row shows the signal name, direction, and boolean attribute badges for power, ground,
 * digital, analog, and active-low attributes. Rows are editable inline before applying.
 *
 * @param jsonData - Raw JSON string representing an array of {@link SignalRow} objects.
 * @returns The rendered table, or null if `jsonData` is missing or cannot be parsed.
 */
export function SignalsTableMarkdownItem({ jsonData }: SignalsTableMarkdownItemProps) {
	const [rows, setRows] = useParsedDirectiveRows(jsonData, parseSignals);
	const [isEditing, setIsEditing] = useState(false);

	if (!rows) {
		return null;
	}

	function updateRow(index: number, patch: Partial<SignalRow>) {
		setRows((prev) => (prev ? prev.map((r, i) => (i === index ? { ...r, ...patch } : r)) : null));
	}

	function updateRowFlag(index: number, key: BooleanSignalKey, value: boolean) {
		setRows((prev) => (prev ? prev.map((r, i) => (i === index ? { ...r, [key]: value } : r)) : null));
	}

	return (
		<>
			<div className='my-2 overflow-x-auto rounded border border-gray-a4 text-sm'>
				<table className='w-full border-collapse'>
					<thead>
						<tr className='bg-gray-a2 text-left text-xs font-semibold text-gray-11'>
							<th className='px-3 py-2'>Signal name</th>
							<th className='px-3 py-2'>Direction</th>
							<th className='px-3 py-2'>Attributes</th>
							<th className='px-3 py-2 text-right'>
								<ButtonBase
									className='font-normal tracking-normal text-gray-11 normal-case hover:text-gray-12'
									onClick={() => setIsEditing((v) => !v)}
								>
									{isEditing ? 'Done' : 'Edit'}
								</ButtonBase>
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((signal, index) => (
							<tr key={index} className={clsx('border-t border-gray-a3', index % 2 === 1 && 'bg-gray-a2')}>
								<td className='px-3 py-1.5 font-mono font-medium'>
									{isEditing ?
										<input
											type='text'
											value={signal.signalName}
											onChange={(e) => updateRow(index, { signalName: e.target.value })}
											className={editInputClass}
										/>
									:	signal.signalName}
								</td>
								<td className='px-3 py-1.5 text-gray-11'>
									{isEditing ?
										<input
											type='text'
											value={signal.direction ?? ''}
											onChange={(e) => updateRow(index, { direction: e.target.value || null })}
											className={editInputClass}
										/>
									:	(signal.direction ?? '—')}
								</td>
								<td className='px-3 py-1.5'>
									{isEditing ?
										<div className='flex flex-wrap gap-x-3 gap-y-1'>
											{BADGES.map(({ key, label }) => (
												<label
													key={key}
													className='flex cursor-pointer items-center gap-1 text-xs font-normal tracking-normal normal-case'
												>
													<input
														type='checkbox'
														checked={signal[key] === true}
														onChange={(e) => updateRowFlag(index, key, e.target.checked)}
													/>
													{label}
												</label>
											))}
										</div>
									:	<span className='flex flex-wrap gap-1'>
											{BADGES.map(
												({ key, label }) =>
													signal[key] === true && (
														<Chip
															key={key}
															color='gray'
															variant='outlined'
															size='small'
															label={
																<span className='flex items-center gap-1'>
																	{/* The hue reinforces the label rather than carrying it: an attribute
																	    colour dark enough to read white text on is darker than this palette
																	    goes. */}
																	<span
																		aria-hidden
																		className='size-1.5 shrink-0 rounded-full'
																		style={{ backgroundColor: attributeColor(key) }}
																	/>
																	{label}
																</span>
															}
														/>
													),
											)}
										</span>
									}
								</td>
								<td />
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<DirectiveApplyBar data={rows} configId='signals' />
		</>
	);
}

/**
 * The indicator hue for a signal attribute, taken from the palette the signal chips use so an
 * attribute reads as the same colour wherever it appears.
 *
 * @param key - The attribute.
 * @returns The hue, falling back to the current text colour for an attribute the palette omits.
 */
function attributeColor(key: BooleanSignalKey): string {
	return ATTRIBUTE_INDICATORS[key]?.color ?? 'currentColor';
}
